/**
 * Fiche d'une organisation (SUPER_ADMIN).
 * Informations, utilisateurs, camions, trajets, licence et journal des actions.
 */
import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  Users,
  Truck,
  Map,
  Key,
  PauseCircle,
  PlayCircle,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Shield,
} from 'lucide-react';
import { adminAPI, organizationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { libelle, nomLisible } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatDateHeure, formatLitres } from '../lib/formats';
import BoutonImportExcel from '../components/fleetImport/BoutonImportExcel';

const TABS = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: Building2 },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'trucks', label: 'Camions', icon: Truck },
  { id: 'trips', label: 'Trajets', icon: Map },
  { id: 'license', label: 'Licence', icon: Key },
  { id: 'audit', label: 'Journal des actions', icon: Shield },
];

// Un trajet terminé ou annulé n'est plus « en cours » (le reste, hors « créé », l'est).
const FIN_DE_TRAJET = ['COMPLETED', 'CANCELLED'];

const Badge = ({ children, color = 'slate' }) => {
  const colors = {
    slate: 'bg-muted text-muted-foreground',
    emerald: 'bg-success/15 text-success',
    rose: 'bg-destructive/15 text-destructive',
    amber: 'bg-flame/15 text-flame-ink',
    sky: 'bg-info/15 text-info',
    violet: 'bg-process/15 text-process',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${colors[color]}`}>
      {children}
    </span>
  );
};


export default function OrganizationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getOrganizationSummary(id);
      setSummary(data);
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de charger l’organisation'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !summary) {
    return (
      <div className="p-8 text-muted-foreground">Chargement…</div>
    );
  }

  const { organization: org, license, stats } = summary;

  const handleSuspend = async () => {
    if (!window.confirm('Suspendre cette organisation ?')) return;
    try {
      await adminAPI.suspendOrganization(id);
      toast.success('Organisation suspendue');
      load();
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de suspendre l’organisation'));
    }
  };

  const handleReactivate = async () => {
    try {
      await adminAPI.reactivateOrganization(id);
      toast.success('Organisation réactivée');
      load();
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de réactiver l’organisation'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette organisation ?')) return;
    try {
      await organizationsAPI.delete(id);
      toast.success('Organisation supprimée');
      navigate('/organizations');
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de supprimer l’organisation'));
    }
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Organisation"
        title={org.name}
        backTo="/organizations"
        backLabel="Retour aux organisations"
        stats={[
          { label: 'Utilisateurs', valeur: stats?.users_count },
          { label: 'Camions', valeur: stats?.trucks_count },
          { label: 'Trajets actifs', valeur: stats?.active_trips },
          { label: 'Recettes (30 jours)', valeur: stats?.revenue_30d || 0, unite: 'FCFA' },
        ]}
        statsPlus={
          <p className="text-sm">
            <span className="text-muted-foreground">Trajets (7 jours) : </span>
            <span className="font-medium tabular-nums">{stats?.trips_7d ?? '—'}</span>
          </p>
        }
        meta={
          <>
            <Badge color={org.is_active ? 'emerald' : 'rose'}>
              {org.is_active ? 'En activité' : 'Suspendue'}
            </Badge>
            {org.type && <Badge color="slate">{libelle('orgType', org.type)}</Badge>}
            {license && <Badge color="violet">Licence {libelle('licensePlan', license.plan)}</Badge>}
            {org.email && (
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                {org.email}
              </span>
            )}
            {org.phone && (
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                {org.phone}
              </span>
            )}
            {(org.address || org.city) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {[org.address, org.city, org.country].filter(Boolean).join(', ')}
              </span>
            )}
          </>
        }
        actions={
          <>
            <BoutonImportExcel organizationId={id} />
            {org.is_active ? (
              <button onClick={handleSuspend} className="btn-secondary px-3 text-flame-ink">
                <PauseCircle className="h-4 w-4" aria-hidden="true" /> Suspendre
              </button>
            ) : (
              <button onClick={handleReactivate} className="btn-secondary px-3 text-success">
                <PlayCircle className="h-4 w-4" aria-hidden="true" /> Réactiver
              </button>
            )}
            <button onClick={handleDelete} className="btn-secondary px-3 text-destructive">
              <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
            </button>
          </>
        }
      />

      <div className="grid gap-3 md:grid-cols-3">
        <OrgDecisionCard
          title="Licence et accès"
          value={license ? libelle('licensePlan', license.plan) : 'Aucune licence'}
          text={license ? `Licence ${libelle('licenseStatus', license.status).toLowerCase()}. Les modules cochés déterminent les pages accessibles.` : 'Créer une licence avant d’ouvrir les accès métier.'}
          tone={license?.status === 'ACTIVE' ? 'emerald' : 'rose'}
          action={() => navigate('/licenses')}
        />
        <OrgDecisionCard
          title="Capacité utilisée"
          value={`${stats?.users_count || 0} sur ${license?.max_users || 'illimité'} utilisateurs`}
          text={`${stats?.trucks_count || 0} sur ${license?.max_trucks || 'illimité'} camions. Surveiller les limites avant d’ajouter beaucoup de comptes ou de camions.`}
          tone={license && ((stats?.users_count || 0) > (license.max_users || Infinity) || (stats?.trucks_count || 0) > (license.max_trucks || Infinity)) ? 'rose' : 'sky'}
          action={() => setTab('license')}
        />
        <OrgDecisionCard
          title="Activité opérationnelle"
          value={`${stats?.active_trips || 0} en cours`}
          text={(stats?.active_trips || 0) ? 'Trajets ou convois en cours: vérifier les blocages, le suivi des camions, le stock et la facturation.' : 'Aucun trajet en cours pour cette organisation.'}
          tone={(stats?.active_trips || 0) ? 'amber' : 'slate'}
          action={() => setTab('trips')}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
                active
                  ? 'border-flame/60 text-flame-ink'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="grid gap-4 md:grid-cols-2">
          <Section title="Informations">
            <Row label="Nom" value={org.name} />
            <Row label="Activité" value={org.type ? libelle('orgType', org.type) : null} />
            <Row label="Email" value={org.email} />
            <Row label="Téléphone" value={org.phone} />
            <Row label="Adresse" value={org.address} />
            <Row label="Ville" value={org.city} />
            <Row label="Pays" value={org.country} />
            <Row label="Créée le" value={formatDateHeure(org.created_at)} />
          </Section>
          <Section title="Licence">
            {license ? (
              <>
                <Row label="Formule" value={<Badge color="violet">{libelle('licensePlan', license.plan)}</Badge>} />
                <Row label="État" value={<Badge color={license.status === 'ACTIVE' ? 'emerald' : 'rose'}>{libelle('licenseStatus', license.status)}</Badge>} />
                <Row label="Utilisateurs maximum" value={license.max_users} />
                <Row label="Camions maximum" value={license.max_trucks} />
                <Row label="Expire le" value={license.expires_at ? formatDate(license.expires_at) : 'Jamais'} />
              </>
            ) : (
              <div className="text-sm text-muted-foreground/70">Aucune licence active</div>
            )}
            <div className="pt-3">
              <button onClick={() => navigate('/licenses')} className="text-sm font-semibold text-flame-ink hover:underline">
                Gérer les licences →
              </button>
            </div>
          </Section>
        </div>
      )}

      {tab === 'users' && (
        <Table
          cle="utilisateurs"
          nom="utilisateurs"
          organisation={id}
          section="users"
          totalInitial={stats?.users_count}
          columns={['Nom', 'Email', 'Rôle', 'État', 'Code d’accès', 'Dernière connexion']}
          toRow={(u) => [
            nomLisible(u.full_name, '—'),
            u.email || '—',
            <Badge key="role" color="sky">{libelle('userRole', u.role)}</Badge>,
            <Badge key="status" color={u.is_active ? 'emerald' : 'rose'}>{u.is_active ? 'Actif' : 'Inactif'}</Badge>,
            u.access_code ? <code key="access-code" className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{u.access_code}</code> : '—',
            formatDateHeure(u.last_login, 'Jamais connecté'),
          ]}
        />
      )}

      {tab === 'trucks' && (
        <Table
          cle="camions"
          nom="camions"
          organisation={id}
          section="trucks"
          totalInitial={stats?.trucks_count}
          columns={['Matricule', 'Marque', 'Modèle', 'Capacité', 'État']}
          toRow={(t) => [
            <span key="matricule" className="font-mono font-semibold">{t.matricule}</span>,
            t.brand || '—',
            t.model || '—',
            t.capacity_liters ? formatLitres(t.capacity_liters) : '—',
            <Badge key="status" color={t.status === 'active' ? 'emerald' : 'slate'}>{libelle('truckStatus', t.status)}</Badge>,
          ]}
        />
      )}

      {tab === 'trips' && (
        <Table
          cle="trajets"
          nom="trajets"
          organisation={id}
          section="trips"
          totalInitial={stats?.trips_total}
          columns={['Camion', 'Départ', 'Arrivée', 'État', 'Début', 'Fin']}
          toRow={(t) => [
            <span key="camion" className="font-mono font-semibold">{t.tractor_plate || '—'}</span>,
            t.loading_depot || t.departure_country || '—',
            t.destination_country || '—',
            <Badge key="status" color={t.status && !FIN_DE_TRAJET.includes(t.status) && t.status !== 'CREATED' ? 'amber' : 'slate'}>
              {libelle('tripStatus', t.status)}
            </Badge>,
            formatDateHeure(t.departure_date || t.created_at),
            formatDateHeure(t.return_date),
          ]}
        />
      )}

      {tab === 'license' && (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          {license ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3>Licence active</h3>
                <Badge color={license.status === 'ACTIVE' ? 'emerald' : 'rose'}>{libelle('licenseStatus', license.status)}</Badge>
              </div>
              <Row label="Formule" value={libelle('licensePlan', license.plan)} />
              <Row label="Utilisateurs (utilisés sur autorisés)" value={`${stats?.users_count || 0} sur ${license.max_users}`} />
              <Row label="Camions (utilisés sur autorisés)" value={`${stats?.trucks_count || 0} sur ${license.max_trucks}`} />
              <Row label="Émise le" value={formatDate(license.issued_at)} />
              <Row label="Expire le" value={license.expires_at ? formatDate(license.expires_at) : 'Jamais'} />
              {license.notes && <Row label="Notes" value={license.notes} />}
              <div className="pt-3">
                <button onClick={() => navigate('/licenses')} className="text-sm font-semibold text-flame-ink hover:underline">
                  Autres actions (renouvellement, facture…) →
                </button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground/70">
              Aucune licence active. <button onClick={() => navigate('/licenses')} className="text-flame-ink hover:underline">Créer une licence</button>
            </div>
          )}
        </div>
      )}

      {tab === 'audit' && <Journal organisation={id} totalInitial={stats?.audit_total} />}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
    <h3 className="mb-3 uppercase text-muted-foreground">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border/60 py-1.5 last:border-0">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className={`text-sm text-foreground ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
  </div>
);

const OrgDecisionCard = ({ title, value, text, tone = 'slate', action }) => {
  const tones = {
    emerald: 'border-success/30 bg-success/10 text-success',
    rose: 'border-destructive/30 bg-destructive/10 text-destructive',
    sky: 'border-info/30 bg-info/10 text-info',
    amber: 'border-flame/30 bg-flame/10 text-flame-ink',
    slate: 'border-border bg-card text-foreground',
  };
  return (
    <div
      className={`rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-center gap-1">
        <p className="text-xs font-bold uppercase tracking-wide opacity-75">{title}</p>
        {text && <Aide titre={title}>{text}</Aide>}
      </div>
      <button type="button" onClick={action} className="block w-full text-left">
        <p className="kpi-value mt-2">{value}</p>
        <p className="mt-3 text-xs font-bold">Voir le détail →</p>
      </button>
    </div>
  );
};

/**
 * Lit une liste de la fiche (comptes, camions, trajets, journal) page par page.
 *
 * Le serveur découpe (`skip`/`limit`) et donne le `total` de toute la liste: la
 * page, sa taille et le nombre de pages viennent de l'adresse (`cle`), jamais
 * d'une liste plafonnée chargée d'avance. Le total du résumé sert de première
 * valeur, le temps que la première page arrive.
 */
function useListeOrganisation({ organisation, section, cle, totalInitial }) {
  const [donnees, setDonnees] = useState({ items: [], total: Number(totalInitial) || 0 });
  const [chargement, setChargement] = useState(true);
  const pagination = usePaginationServeur(donnees.total, { cle, reinitialiser: organisation });
  const { skip, limit } = pagination;

  useEffect(() => {
    let annule = false;
    setChargement(true);
    adminAPI
      .getOrganizationList(organisation, section, { skip, limit })
      .then((reponse) => {
        if (annule) return;
        const items = Array.isArray(reponse?.items) ? reponse.items : [];
        setDonnees({ items, total: Number(reponse?.total ?? items.length) || 0 });
      })
      .catch((e) => {
        if (annule) return;
        toast.error(messageErreur(e, 'Impossible de charger la liste'));
        setDonnees({ items: [], total: 0 });
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });
    return () => {
      annule = true;
    };
  }, [organisation, section, skip, limit]);

  return { pagination, items: donnees.items, chargement };
}

const Journal = ({ organisation, totalInitial }) => {
  const { pagination, items, chargement } = useListeOrganisation({
    organisation, section: 'audit', cle: 'journal', totalInitial,
  });
  return (
    <div className="space-y-2" aria-busy={chargement}>
      {items.length ? (
        items.map((log) => (
          <div key={log.id || log._id || log.timestamp} className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-3 shadow-sm">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-readout px-1.5 py-0.5 text-[10px] font-bold text-white">{libelle('auditAction', log.action)}</span>
                <span className="text-sm text-muted-foreground">{libelle('auditEntity', log.entity_type)}</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Par {nomLisible(log.changed_by_name, 'le système')}
              </div>
            </div>
            <div className="text-xs text-muted-foreground/70">
              {formatDateHeure(log.timestamp)}
            </div>
          </div>
        ))
      ) : (
        <div className="py-8 text-center text-sm text-muted-foreground/70">
          {chargement ? 'Chargement…' : 'Aucune action enregistrée pour le moment'}
        </div>
      )}
      <BarrePagination pagination={pagination} nom="actions" />
    </div>
  );
};

const Table = ({ columns, toRow, cle, nom, organisation, section, totalInitial }) => {
  const { pagination, items, chargement } = useListeOrganisation({ organisation, section, cle, totalInitial });
  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm" aria-busy={chargement}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left readout-label">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="px-4 py-3">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="p-8 text-center text-muted-foreground/70">
                    {chargement ? 'Chargement…' : 'Rien à afficher pour le moment'}
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr
                    key={item.id ?? `${pagination.premiere + index}`}
                    className={`hover:bg-muted/50 ${chargement ? 'opacity-60' : ''}`}
                  >
                    {toRow(item).map((cell, j) => (
                      <td key={columns[j]} className="px-4 py-3">{cell}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <BarrePagination pagination={pagination} nom={nom} />
    </>
  );
};
